import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  AutovehicleSpecsStore,
  PotreeGenerateAreaStore,
  usePathModeStore,
  usePotreeStore,
  useHeadlandWorkAreaStore,
} from "../Store/PotreeStore";
import * as turf from "@turf/turf";
import { useLastAreaMeasurementStore } from "../Store/PotreeStore";
import { useParams } from "react-router-dom";
import * as THREE from "three";
import { generateSwaths } from "../util/Generator";
import {
  visualizeBezierControlPoints,
  updateBezierCurve,
  converterToBezier,
  visualizePoints,
} from "../util/bezierEditor";
import {
  createCTurnPath,
  createXTurnPath,
  FullPathGeneration,
  orderedSwaths,
  toEPSG5186,
  toWGS84,
} from "../util/pathConnector";
import {
  distancePointToSegment,
  findNearestEdge,
  makeTurnPoint,
  TurnPoint,
  generateHeadland,
  exportMeasurementToGeoJSON,
} from "../util/pathGeometry";
import {
  createRhombusMesh,
  clearRhombusMarkers,
  getMinZ as getMinZFromViewer,
} from "../util/sceneObjects";
import {
  getClosestPointWGS84,
  getClosetIntersection,
  createStartPointMesh,
} from "../util/startPointPicker";
import { useAreaMeasurement } from "../hooks/useAreaMeasurement";
import { useTractorAnimation } from "../hooks/useTractorAnimation";
import IntroduceGeneratePath from "../components/PotreeUtil/IntroduceGeneratePath";
import { useBezierDrag } from "../hooks/useBezierDrag";
import { usePotreeInit } from "../hooks/usePotreeInit";

const PotreeViewerContainer = styled.div`
  position: relative;
  .potree-render-area {
    position: absolute;
    z-index: 5;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

export default function PotreeViewer3() {
  const containerRef = useRef(null);
  const { projectId, fileId } = useParams();
  const { isLoaded } = usePotreeInit({ containerRef, fileId });

  const { potreeViewer, editMode, selectedPointMode, setSelectedPointMode } =
    usePotreeStore();

  const {
    IsStartAreaGenerate,
    pathMode,
    setGradientCover,
    direction,
    safetyMargin,
    setMinZ,
    elevationRangeOffset,
    setIsPathGenerated,
  } = PotreeGenerateAreaStore();

  const {
    selectedAutovehicleSpecs,
    AutovehicleSpecs,
    interval,
    size,
    WorkMode,
    setSeedPosition,
  } = AutovehicleSpecsStore();

  const { fullPathStore, setFullPathStore } = usePathModeStore();
  const [generatedFullPath, setGeneratedFullPath] = useState(null); // 생성된 전체 경로 저장
  const [bezierCurves, setBezierCurves] = useState([]); // 베지어 곡선 저장

  const getMinZ = () => getMinZFromViewer(potreeViewer);

  const startPointRef = useRef(null);
  const endPointRef = useRef(null);
  const selectedStartPointRef = useRef(null);
  const finalPathRef = useRef([]);
  const handlePointCloudPickRef = useRef(null);
  const [fullPath, setFullPath] = useState([]);

  // 지형 파악 그라디언트 적용
  const GradientCover = () => {
    if (
      !potreeViewer ||
      !potreeViewer.scene ||
      !potreeViewer.scene.pointclouds ||
      potreeViewer.scene.pointclouds.length === 0
    ) {
      console.warn(
        "PotreeViewer 또는 포인트 클라우드가 아직 준비되지 않았습니다.",
      );
      return;
    }
    const pointCloud = potreeViewer.scene.pointclouds[0];
    const box = pointCloud.boundingBox
      .clone()
      .applyMatrix4(pointCloud.matrixWorld);

    const minZ = box.min.z;
    const maxZ = box.max.z;

    const material = pointCloud.material;

    const isGradientActive =
      material.activeAttributeName === "elevation" &&
      material.useElevation === true;

    if (!isGradientActive) {
      material.pointSizeType = window.Potree.PointSizeType.ADAPTIVE;
      material.gradient = window.Potree.Gradients.TURBO;
      material.activeAttributeName = "elevation";
      material.useElevation = true;

      material.elevationRange = [minZ, minZ + elevationRangeOffset];

      material.needsUpdate = true;
      pointCloud.material = material;
    } else {
      material.activeAttributeName = "rgba"; // 또는 'rgb'
      material.useElevation = false;
    }

    potreeViewer.render();
  };

  // 그라디언트 기능 스토어에 저장하여 다른 컴포넌트에서 버튼화
  useEffect(() => {
    setGradientCover(() => GradientCover());
    setMinZ(() => getMinZ());
  }, [potreeViewer, setGradientCover, setMinZ]);

  // elevationRangeOffset 변경 시 실시간으로 그라데이션 범위 업데이트
  useEffect(() => {
    if (!potreeViewer?.scene?.pointclouds?.length) return;
    const pointCloud = potreeViewer.scene.pointclouds[0];
    const material = pointCloud.material;
    if (material.activeAttributeName !== "elevation" || !material.useElevation)
      return;
    const box = pointCloud.boundingBox
      .clone()
      .applyMatrix4(pointCloud.matrixWorld);
    material.elevationRange = [box.min.z, box.min.z + elevationRangeOffset];
    material.needsUpdate = true;
    potreeViewer.render();
  }, [elevationRangeOffset, potreeViewer]);

  // 선택된 차량 스펙 정보
  const vehicleSpecs = AutovehicleSpecs[selectedAutovehicleSpecs];

  // safetyMargin은 PotreeGenerateAreaStore에서 관리 (UI에서 조절 가능)
  const swathsRef = useRef([]);
  const headlandRef = useRef(null);

  // 수정모드 관련 함수- ------------------------------------------------------------
  // 2. 수정버튼으로 수정모드 활성화 & 제어점 시각화 함수 활성화
  useEffect(() => {
    if (!potreeViewer || !bezierCurves[0]?.points?.length) return;

    const scene = potreeViewer.scene.scene;

    // 기존 제어점 제거
    const existingControls = scene.getObjectByName("bezier-controls");
    if (existingControls) {
      scene.remove(existingControls);
    }

    if (editMode) {
      // 수정 모드: 제어점 표시
      visualizeBezierControlPoints(bezierCurves, potreeViewer, getMinZ);
    }
  }, [editMode, bezierCurves, potreeViewer]);

  const { onMouseDown, onMouseMove, onMouseUp } = useBezierDrag(
    potreeViewer,
    editMode,
    bezierCurves,
    setBezierCurves,
  );

  const {
    measurementsRef,
    isMeasuring,
    setIsMeasuring,
    startAreaMeasurement,
    endAreaMeasurement,
  } = useAreaMeasurement(potreeViewer);

  const { runAnimation } = useTractorAnimation(
    potreeViewer,
    getMinZ,
    vehicleSpecs,
    selectedStartPointRef,
  );

  const createWorkAreaPolygon = () => {
    // Potree 실시간 좌표를 우선 사용 (꼭지점 드래그 반영)
    const latest = potreeViewer?.scene?.measurements?.at?.(-1);
    const workArea = latest
      ? latest.points.map((p) => ({
          x: p.position.x,
          y: p.position.y,
          z: p.position.z,
        }))
      : measurementsRef.current.slice(0);
    if (workArea.length === 0) return null;
    workArea.push(workArea[0]);
    const workAreaWGS84 = [];
    workArea.forEach((point) => {
      if (point && point.x !== undefined && point.y !== undefined) {
        workAreaWGS84.push(toWGS84([point.x, point.y]));
      }
    });
    if (workAreaWGS84.length === 0) return null;
    return turf.polygon([workAreaWGS84]);
  };

  // 포인트 클라우드 선택 핸들러
  const handlePointCloudPick = (event) => {
    // 우클릭(button === 2)은 영역 지정 완료용이므로 출발점 지정에서 제외
    if (event.button === 2) return;

    if (!potreeViewer || !measurementsRef.current.length) return;

    // 함수 실행 ------------------------------------------------------------
    const closestPointData = getClosestPointWGS84(
      event,
      potreeViewer,
      selectedPointMode,
    ); // 클릭한 포인터 찾기 (3D 좌표 포함)
    if (!closestPointData || !closestPointData.wgs84) return;

    const closestPointWGS84 = closestPointData.wgs84;
    const clickedPoint3D = closestPointData.point3d; // 클릭한 포인트의 실제 z값

    const workAreaPolygon = createWorkAreaPolygon(); // 측정 영역을 폴리곤으로 만들기
    if (!workAreaPolygon) return;

    // 측정 영역 내부에 가장 가까운 포인트가 있는지 확인
    const isInside = turf.booleanPointInPolygon(
      closestPointWGS84,
      workAreaPolygon,
    );

    if (isInside) {
      // 가장 가까운 교차점 찾기
      const bestHit = getClosetIntersection(closestPointWGS84, workAreaPolygon);

      const bestPointEPSG5186 = toEPSG5186(bestHit.point);
      const workAreaPolygonEPSG5186 =
        workAreaPolygon.geometry.coordinates[0].map((point) =>
          toEPSG5186(point),
        );

      // 출발점 시각화
      if (bestHit) {
        createStartPointMesh(
          bestHit.point,
          potreeViewer,
          selectedStartPointRef,
          getMinZ,
        ); // 출발점 시각화
        selectedStartPointRef.current = {
          x: bestPointEPSG5186[0],
          y: bestPointEPSG5186[1],
          z: clickedPoint3D.z, // 클릭한 포인트의 실제 z값 저장
          edgeIndex: bestHit.edgeIndex, // 출발점이 포함된 폴리곤의 변의 인덱스 획득
          workAreaPolygon: workAreaPolygonEPSG5186,
        }; // 선택한 출발점 저장

        GeneratePath(selectedStartPointRef.current);
      }
    } else {
      console.log("측정 영역 외부");
    }
  };

  // stale closure 방지: 최신 콜백을 ref로 유지
  handlePointCloudPickRef.current = handlePointCloudPick;

  // ------------------------------------------------------------
  // 순수 회전 반경 계산 함수
  function caculateturningRadius() {
    const rad = (vehicleSpecs.steeringAngle * Math.PI) / 180;
    const R = vehicleSpecs.wheelBase / Math.tan(rad);

    return R.toFixed(2);
  }

  // ------------------------------------------------------------

  useEffect(() => {
    if (!fullPath.length) return;
    // 출발점과 도착점 저장
    startPointRef.current = fullPath.features[0].geometry.coordinates[0];
    endPointRef.current =
      fullPath.features[fullPath.features.length - 1].geometry.coordinates[
        fullPath.features[fullPath.features.length - 1].geometry.coordinates
          .length - 1
      ];
    const polygon = selectedStartPointRef.current.workAreaPolygon;
    const N = polygon.length;

    // 출발점과 도착점이 포함된 폴리곤의 변의 인덱스 획득
    const startEdgeIndex = findNearestEdge(
      [startPointRef.current.x, startPointRef.current.y],
      polygon,
    );
    const endEdgeIndex = findNearestEdge(
      [endPointRef.current.x, endPointRef.current.y],
      polygon,
    );
    const selectedEdgeIndex = selectedStartPointRef.current.edgeIndex;

    const turningRadius = caculateturningRadius();
    const turnPoints = TurnPoint(
      polygon,
      headlandRef.current,
      Number(turningRadius),
    );

    // 출발점부터 각 도착점 까지의 거리 계산 함수
    function dist2(a, b) {
      const dx = a[0] - b[0];
      const dy = a[1] - b[1];
      const dz = (a[2] ?? 0) - (b[2] ?? 0);
      return dx * dx + dy * dy + dz * dz;
    }

    const selectedArray = [
      selectedStartPointRef.current.x,
      selectedStartPointRef.current.y,
    ];
    const startArray = [startPointRef.current.x, startPointRef.current.y];
    const endArray = [endPointRef.current.x, endPointRef.current.y];
    let EC, ES;
    let goalPoint;
    // 경로의 두 끝점 중 더 가까운 쪽과 출발점을 연결
    if (dist2(selectedArray, startArray) < dist2(selectedArray, endArray)) {
      EC = selectedEdgeIndex;
      ES = startEdgeIndex;
      goalPoint = startPointRef.current;
    } else {
      EC = selectedEdgeIndex;
      ES = endEdgeIndex;
      goalPoint = endPointRef.current;
    }

    const distForward = (ES - EC + N) % N;
    const distBackward = (EC - ES + N) % N;
    const useForward = distForward <= distBackward;
    const neededWCount = Math.min(distForward, distBackward); // 필요한 회전 포인트 개수

    let Wlist = [];
    if (useForward) {
      for (let i = 1; i <= distForward; i++) {
        Wlist.push(turnPoints[(EC + i) % N]);
      }
    } else {
      for (let i = 1; i <= distBackward; i++) {
        Wlist.push(turnPoints[(EC - i + N) % N]);
      }
    }

    finalPathRef.current = [
      selectedArray,
      ...Wlist,
      [goalPoint.x, goalPoint.y],
    ];

    const newConnectionCoords = finalPathRef.current;

    const connectionFeature = {
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: newConnectionCoords,
      },
    };

    const oldfullPath = fullPath;

    const newFullPath = {
      type: "FeatureCollection",
      features: [connectionFeature, ...oldfullPath.features],
    };

    const bezierCurves = converterToBezier(newFullPath);

    setBezierCurves(bezierCurves);
  }, [generatedFullPath]);

  // 5. 제어점 이동 이벤트 처리
  useEffect(() => {
    if (!potreeViewer) return;
    const canvas = potreeViewer.renderer.domElement;

    const masterDown = (e) => {
      // 우클릭(button === 2)은 영역 지정 완료용이므로 출발점 지정에서 제외
      if (e.button === 2) return;
      console.log("masterDown 진입", { editMode, selectedPointMode });
      if (editMode) onMouseDown(e);
      else if (selectedPointMode) handlePointCloudPickRef.current(e);
    };

    const masterMove = (e) => {
      if (editMode) onMouseMove(e);
    };

    const masterUp = (e) => {
      if (editMode) onMouseUp(e);
    };
    canvas.addEventListener("mousedown", masterDown);
    canvas.addEventListener("mousemove", masterMove);
    canvas.addEventListener("mouseup", masterUp);

    return () => {
      canvas.removeEventListener("mousedown", masterDown);
      canvas.removeEventListener("mousemove", masterMove);
      canvas.removeEventListener("mouseup", masterUp);
    };
  }, [editMode, potreeViewer, bezierCurves, selectedPointMode]);

  // 경로 시각화 함수
  useEffect(() => {
    if (!potreeViewer || !generatedFullPath || !bezierCurves.length) return;

    visualizePath(
      swathsRef.current,
      headlandRef.current,
      bezierCurves,
      getMinZ(),
    );
  }, [potreeViewer, bezierCurves]);
  // ------------------------------------------------------------

  // 헤드랜드 너비 계산 함수
  function caculateHeadlandWidth(safetyMargin) {
    // R : 회전반경 계산 함수
    const rad = (vehicleSpecs.steeringAngle * Math.PI) / 180;
    const R = vehicleSpecs.wheelBase / Math.tan(rad);

    const minTurningWidth = (R + safetyMargin).toFixed(2);

    return minTurningWidth;
  }
  // 헤드랜드 너비 계산 (미터 단위)
  const headlandWidth = caculateHeadlandWidth(safetyMargin);

  const turningRadius = caculateturningRadius();

  // 경로 시각화 함수 (유지)
  const visualizePath = (swaths, headlandCoords, bezierCurves, minZ) => {
    if (!potreeViewer) return;
    const scene = potreeViewer.scene.scene;
    let group = scene.getObjectByName("full_path_group");

    if (!group) {
      group = new THREE.Group();
      group.name = "full_path_group";
      scene.add(group);
    } else {
      // 이전 라인 정리
      while (group.children.length) {
        const child = group.children.pop();
        child.geometry?.dispose?.();
        child.material?.dispose?.();
      }
    }

    const removeExisting = (name) => {
      const obj = scene.getObjectByName(name);
      if (obj) scene.remove(obj);
    };

    // 라인 생성 함수 - 출발점의 z값 기준 사용
    const createLine = (coords, color, name, close = false) => {
      // 출발점의 z값 가져오기 (없으면 최소값 사용)
      const baseZ = selectedStartPointRef.current?.z ?? getMinZ();
      const offsetZ = baseZ + 2.5; // 출발점 z값 + 2.5m

      const points = coords.map(([x, y]) => new THREE.Vector3(x, y, offsetZ));
      if (close) points.push(points[0]);
      const geom = new THREE.BufferGeometry().setFromPoints(points);
      const mat = new THREE.LineBasicMaterial({
        color,
        linewidth: 2,
      });
      const line = new THREE.Line(geom, mat);
      line.name = name;
      return line;
    };

    try {
      // 기존 시각화 제거
      removeExisting("swaths");
      removeExisting("headland_area");
      removeExisting("full_path");

      // 스와스 라인들 시각화
      if (swaths?.length) {
        const group = new THREE.Group();
        group.name = "swaths";

        swaths.forEach((swath, index) => {
          const line = createLine(
            swath.geometry.coordinates,
            index % 2 === 0 ? 0x00ff00 : 0xff0000,
            `swath_${index}`,
          );

          group.add(line);
        });
      }

      bezierCurves.forEach((curve, idx) => {
        const sampled = curve.samples ?? curve.points;
        if (!sampled?.length) return;

        const coords = sampled.map(({ x, y }) => [x, y]);

        const isClosed =
          coords.length > 1 &&
          coords[0][0] === coords.at(-1)[0] &&
          coords[0][1] === coords.at(-1)[1];

        if (isClosed) coords.pop();

        const line = createLine(coords, 0xffff00, `full_path_${idx}`);
        group.add(line);
      });
    } catch (err) {
      console.warn("경로 시각화 오류:", err);
    }
  };

  function placeRhombusAlongCoords({
    coords, // [[x, y], [x, y], ...]
    interval = 2, // 간격 (m)
    scene, // potreeViewer.scene.scene
    size = 2, // 마름모 크기
    groupName = "ridge-rhombus-markers",
  }) {
    if (!Array.isArray(coords) || coords.length < 2) return [];

    const toVec3 = (x, y, z = 0) => new THREE.Vector3(x, y, z);
    const up = new THREE.Vector3(0, 0, 1);

    // 기존 그룹 정리
    const existing = scene.getObjectByName(groupName);
    if (existing) {
      existing.traverse((child) => {
        child.geometry?.dispose?.();
        child.material?.dispose?.();
      });
      scene.remove(existing);
    }

    const group = new THREE.Group();
    group.name = groupName;
    scene.add(group);

    const markerPositions = [];

    let accumulated = 0;
    let distanceSinceLast = 0;

    // 출발점의 z값 기준 사용
    const baseZ = selectedStartPointRef.current?.z ?? getMinZ();
    const offsetZ = baseZ + 2.5; // 출발점 z값 + 2.5m

    for (let i = 0; i <= coords.length - 1; i++) {
      const startPoint = coords[i][0]; // [x, y]
      const endPoint = coords[i][1]; // [x, y]
      const start = toVec3(startPoint[0], startPoint[1], offsetZ);
      const end = toVec3(endPoint[0], endPoint[1], offsetZ);
      const segmentLength = start.distanceTo(end);

      if (segmentLength === 0) continue;

      let remaining = interval - distanceSinceLast;

      while (remaining <= segmentLength) {
        const t = remaining / segmentLength;

        const point = start.clone().lerp(end, t);

        const rhombus = createRhombusMesh(size);
        rhombus.position.copy(point);

        const dir = end.clone().sub(start).normalize();
        const target = point.clone().add(dir);
        rhombus.lookAt(target);
        rhombus.rotateOnAxis(up, Math.PI / 4);

        group.add(rhombus);

        markerPositions.push({ x: point.x, y: point.y, z: point.z });

        remaining += interval;
      }

      distanceSinceLast = segmentLength - (remaining - interval);

      if (distanceSinceLast >= interval) distanceSinceLast %= interval;
      accumulated += segmentLength;
    }
    setSeedPosition(markerPositions);
    return markerPositions;
  }

  // 경로 생성 함수
  const GeneratePath = async (selectedStartPoint) => {
    try {
      // 헤드랜드 데이터 로드 함수
      const LoadHeadland = async () => {
        try {
          const measurements = potreeViewer?.scene?.measurements;
          if (measurements?.length === 0) {
            alert("먼저 측정 영역을 지정해주세요!");
            return;
          }

          const fieldData = exportMeasurementToGeoJSON(measurements[0]);
          const headlandData = generateHeadland(fieldData, headlandWidth);
          return headlandData;
        } catch (err) {
          console.warn("Fields2CoverPath 로딩 오류:", err);
        }
      };

      function clearProfiles(viewer) {
        if (!viewer?.scene) return;

        const scene = viewer.scene;
        const sceneMain = scene.scene;
        const sceneProfile = scene.sceneProfiles || scene.scene_profile;

        const removeProfile = (profile) => {
          const removeAll = (arr) => {
            arr?.forEach((obj) => {
              sceneMain.remove(obj);
              sceneProfile?.remove(obj);
              if (obj.material) obj.material.dispose?.();
              if (obj.geometry) obj.geometry.dispose?.();
            });
          };

          removeAll(profile.spheres);
          removeAll(profile.edges);
          removeAll(profile.boxes);
          removeAll(profile.children);

          // 라벨 텍스처 정리
          profile.edgeLabels?.forEach((l) => l.texture?.dispose?.());
          profile.angleLabels?.forEach((l) => l.texture?.dispose?.());
          profile.coordinateLabels?.forEach((l) => l.texture?.dispose?.());

          profile.dispose?.();
        };

        const allProfiles = [
          ...(scene.profiles || []),
          ...(scene.profileMeasurements || []),
        ];

        allProfiles.forEach(removeProfile);

        scene.profiles = [];
        scene.profileMeasurements = [];
        if (scene.profileTools) scene.profileTools.profiles = [];

        viewer.profileWindowController?.setProfile(null);
        viewer.profileWindow?.close?.();
      }

      const elevationProfile = (reverseCodeSwaths) => {
        clearProfiles(potreeViewer);

        reverseCodeSwaths.forEach((coords, i) => {
          const profile = new window.Potree.Profile();
          profile.name = `Profile ${i}`;
          profile.setWidth(vehicleSpecs.width - 0.2);

          // 출발점의 z값 기준 사용
          const baseZ = selectedStartPointRef.current?.z ?? getMinZ();
          const offsetZ = baseZ + 2.5; // 출발점 z값 + 2.5m

          coords.forEach(([x, y], i) => {
            profile.addMarker(new THREE.Vector3(x, y, offsetZ));
          });

          potreeViewer.scene.addProfile(profile);
          profile.spheres.forEach((sphere) => {
            sphere.visible = false;
          });
        });
      };

      // 헤드랜드 데이터 로드
      const headlandData = await LoadHeadland();

      if (!headlandData) return;

      // 기계 스펙 간격으로 스와스 생성
      const finalSwaths = generateSwaths(
        headlandData.headland,
        vehicleSpecs.width,
        direction,
      );

      // 스와스 정렬 (경로 모드에 따라 역방향 정렬)
      const reverseCodeSwaths = orderedSwaths(finalSwaths, pathMode);

      // 전체 경로 생성
      let fullPath = FullPathGeneration(
        reverseCodeSwaths,
        pathMode === "C-Turn" ? 0.01 : headlandWidth,
        turningRadius,
        16,
        pathMode,
        4,
        headlandData,
        selectedStartPoint,
      );

      // 경로 모드에 따라 곡선 경로 생성
      if (pathMode === "C-Turn") {
        fullPath = createCTurnPath(
          fullPath,
          turningRadius,
          headlandData.headland,
          headlandData.work_area,
          headlandData.turning,
          visualizePoints,
          potreeViewer,
          getMinZ,
        );
      } else if (pathMode === "X-Turn") {
        fullPath = createXTurnPath(
          fullPath,
          turningRadius,
          headlandData.headland,
          headlandData.work_area,
          headlandData.turning,
        );
      } else {
        alert("경로 모드를 선택해주세요!");
        return;
      }

      runAnimation(fullPath);

      // 베지어 곡선 변환
      const bezierCurves = converterToBezier(fullPath);

      swathsRef.current = finalSwaths;
      headlandRef.current = headlandData.headland;
      setBezierCurves(bezierCurves);
      setGeneratedFullPath(fullPath);
      setFullPathStore(fullPath);
      setIsPathGenerated(true);

      // 헤드랜드 및 작업 영역 데이터를 Store에 저장 (다운로드용)
      const { setHeadlandData, setWorkAreaPolygon } =
        useHeadlandWorkAreaStore.getState();
      setHeadlandData(headlandData);

      // 작업 영역 폴리곤 저장 (measurementsRef는 이미 EPSG5186 좌표)
      if (measurementsRef.current && measurementsRef.current.length > 0) {
        const workAreaCoords = measurementsRef.current.map((point) => [
          point.x,
          point.y,
        ]);
        // 폴리곤 닫기
        if (
          workAreaCoords.length > 0 &&
          (workAreaCoords[0][0] !==
            workAreaCoords[workAreaCoords.length - 1][0] ||
            workAreaCoords[0][1] !==
              workAreaCoords[workAreaCoords.length - 1][1])
        ) {
          workAreaCoords.push(workAreaCoords[0]);
        }
        setWorkAreaPolygon(workAreaCoords);
      }

      // 두둑 시각화
      elevationProfile(reverseCodeSwaths);

      WorkMode === "seeding"
        ? placeRhombusAlongCoords({
            coords: reverseCodeSwaths,
            interval: interval,
            scene: potreeViewer.scene.scene,
            size: size,
          })
        : clearRhombusMarkers(potreeViewer.scene.scene);
    } catch (error) {
      console.warn("경로 생성 오류:", error);
    }
  };

  const SelectedStartPoint = () => {
    setSelectedPointMode(true);
  };

  return (
    <PotreeViewerContainer className="potree-container">
      <div ref={containerRef} className="potree-render-area" />
      {IsStartAreaGenerate && (
        <IntroduceGeneratePath
          viewer={potreeViewer}
          startAreaMeasurement={startAreaMeasurement}
          endAreaMeasurement={endAreaMeasurement}
          GeneratePath={GeneratePath}
          isMeasuring={isMeasuring}
          setIsMeasuring={setIsMeasuring}
          SelectedStartPoint={SelectedStartPoint}
        />
      )}
    </PotreeViewerContainer>
  );
}
